import type { Rectangle } from 'pixi.js';
import { createLinearGradient } from '../../../../libs/gradient';
import type { GradientPointCoords } from '../../../../libs/gradient';
import { DEFAULT_TEXT_ALPHA, getTotalBounds } from '../textMetricsHelpers';
import { DynamicXDirection, DynamicYDirection } from '../../../../enums';
import type { RgbaString } from '../../../../types';
import { Config } from '../../../../video-data/config';
import { clampCornerRadii } from '../../../../libs/roundedRectPath';
import type { TextDynamicBoxBaseRendererSettings, Padding } from './TextDynamicBoxBaseRenderer';
import { TextDynamicBoxBaseRenderer } from './TextDynamicBoxBaseRenderer';
import type { TextDynamicGraphicAnimationRenderer } from './TextDynamicGraphicAnimationRenderer';

type Radii = [number, number, number, number];

export interface TextDynamicBoxRendererSettings extends TextDynamicBoxBaseRendererSettings {
    type: typeof Config.BOX_GRAPHIC;
    // rounded corners
    topLeftRadius: number;
    topRightRadius: number;
    bottomRightRadius: number;
    bottomLeftRadius: number;

    // padding
    paddingTop: number;
    paddingBottom: number;
    paddingLeft: number;
    paddingRight: number;

    // stroke
    strokeStyle: RgbaString;
    strokeWidth: number;

    // animation
    xDirection: DynamicXDirection;
    yDirection: DynamicYDirection;
    xProgress: number;
    yProgress: number;
    fill: RgbaString;

    // gradient fill
    fillType: 'solid' | 'linear';
    gradientStartStop: RgbaString;
    gradientEndStop: RgbaString;
    gradientStartPoint: GradientPointCoords;
    gradientEndPoint: GradientPointCoords;
}

type AnimatableBoxProperties = (typeof TextDynamicBoxRenderer.animatableProperties)[number];
export enum AnimatableNonGeometricBoxProperties {
    xProgress = 'xProgress',
    yProgress = 'yProgress',
}

/**
 * Renders a rounded rectangle behind a block of text.
 */
export class TextDynamicBoxRenderer extends TextDynamicBoxBaseRenderer {
    override readonly type = Config.BOX_GRAPHIC;
    topLeftRadius = 0;
    topRightRadius = 0;
    bottomRightRadius = 0;
    bottomLeftRadius = 0;
    paddingTop = 0;
    paddingBottom = 0;
    paddingLeft = 0;
    paddingRight = 0;
    strokeStyle: RgbaString = 'rgba(255,255,255,0)';
    strokeWidth = 0;
    fill: RgbaString = 'rgba(255,0,255,1)';
    xDirection = DynamicXDirection.right;
    yDirection = DynamicYDirection.bottom;
    xProgress = 1.0;
    yProgress = 1.0;
    fillType: 'solid' | 'linear' = 'solid';
    gradientStartStop: RgbaString = 'rgba(255,0,0,1)';
    gradientEndStop: RgbaString = 'rgba(0,0,255,1)';
    gradientStartPoint: GradientPointCoords = { x: 0, y: 0.5 };
    gradientEndPoint: GradientPointCoords = { x: 1, y: 0.5 };

    override animationConfigurations: TextDynamicGraphicAnimationRenderer<AnimatableBoxProperties>[] =
        [];

    override apply(settings: TextDynamicBoxRendererSettings) {
        super.apply(settings);
        this.topLeftRadius = settings.topLeftRadius;
        this.topRightRadius = settings.topRightRadius;
        this.bottomLeftRadius = settings.bottomLeftRadius;
        this.bottomRightRadius = settings.bottomRightRadius;
        this.paddingTop = settings.paddingTop;
        this.paddingBottom = settings.paddingBottom;
        this.paddingLeft = settings.paddingLeft;
        this.paddingRight = settings.paddingRight;
        this.strokeStyle = settings.strokeStyle;
        this.strokeWidth = settings.strokeWidth;
        this.xDirection = settings.xDirection;
        this.yDirection = settings.yDirection;
        this.xProgress = settings.xProgress;
        this.yProgress = settings.yProgress;
        this.fill = settings.fill;
        this.fillType = settings.fillType;
        this.gradientStartStop = settings.gradientStartStop;
        this.gradientEndStop = settings.gradientEndStop;
        this.gradientStartPoint = settings.gradientStartPoint;
        this.gradientEndPoint = settings.gradientEndPoint;
    }

    #createLinearGradient(context: CanvasRenderingContext2D, bounds: Rectangle): CanvasGradient {
        return createLinearGradient(context, {
            startX: bounds.x + this.gradientStartPoint.x * bounds.width,
            startY: bounds.y + this.gradientStartPoint.y * bounds.height,
            endX: bounds.x + this.gradientEndPoint.x * bounds.width,
            endY: bounds.y + this.gradientEndPoint.y * bounds.height,
            startStop: this.gradientStartStop,
            endStop: this.gradientEndStop,
        });
    }

    /**
     * Returns border radii (scaled if necessary so that the radii fit within the bounds).
     */
    #getClampedRadiiForBounds(bounds: Rectangle): Radii {
        const { topLeftRadius, topRightRadius, bottomRightRadius, bottomLeftRadius } =
            clampCornerRadii(
                {
                    topLeftRadius: this.topLeftRadius,
                    topRightRadius: this.topRightRadius,
                    bottomRightRadius: this.bottomRightRadius,
                    bottomLeftRadius: this.bottomLeftRadius,
                },
                bounds.width,
                bounds.height,
            );

        return [topLeftRadius, topRightRadius, bottomRightRadius, bottomLeftRadius];
    }

    /**
     * Calculates the padding around the box. This is used to determine the bounds of the box so we
     * also include the stroke width in the padding.
     */
    override _getPadding(): Padding {
        // The stroke is aligned to the center of the path, so only half goes outside the bounds.
        const halfStrokeWidth = this.strokeWidth / 2;
        return {
            top: this.paddingTop + halfStrokeWidth,
            bottom: this.paddingBottom + halfStrokeWidth,
            left: this.paddingLeft + halfStrokeWidth,
            right: this.paddingRight + halfStrokeWidth,
        };
    }

    override _getLineWidth() {
        return this.strokeWidth;
    }

    override _getBounds() {
        return getTotalBounds(this._levelBounds);
    }

    /** The list of properties that can be animated during draw */
    static get animatableProperties() {
        return [
            ...TextDynamicBoxBaseRenderer.baseAnimatableProperties,
            ...Object.values(AnimatableNonGeometricBoxProperties),
        ];
    }

    static get animatableGeometricProperties() {
        return TextDynamicBoxBaseRenderer.baseAnimatableGeometricProperties;
    }

    override draw(
        context: CanvasRenderingContext2D,
        inclusionFilterByWordIndex: ((ind: number) => boolean) | undefined,
    ) {
        this._levelBounds.forEach((_, i) => {
            if (inclusionFilterByWordIndex && !inclusionFilterByWordIndex(i)) {
                return;
            }
            const [bounds, alpha] = this._applyAnimationToIndex(i);
            const clampedRadii = this.#getClampedRadiiForBounds(bounds);

            // Half of the stroke goes beyond the bounds, so we adjust the bounds so that when we stroke
            // the rectangle after drawing it, it will match the final bounds.
            const shouldStroke = this.strokeWidth > 0 && bounds.width > 0 && bounds.height > 0;
            if (shouldStroke) {
                bounds.x += this.strokeWidth / 2;
                bounds.y += this.strokeWidth / 2;
                bounds.width -= Math.min(this.strokeWidth, bounds.width);
                bounds.height -= Math.min(this.strokeWidth, bounds.height);
            }
            context.save();
            context.globalAlpha = alpha;
            const fillStyle =
                this.fillType === 'linear'
                    ? this.#createLinearGradient(context, bounds)
                    : this.fill;
            drawRoundRect(context, bounds, clampedRadii, fillStyle);
            if (shouldStroke) {
                drawStroke(context, this.strokeWidth, this.strokeStyle);
            }
            context.restore();
        });
    }

    /**
     * Apply the clipping path for the element at the given index. For example, if this box is for the line level, and
     * there are 3 lines, then index 1 will apply the clipping path for the 2nd line.
     */
    override clipElement(context: CanvasRenderingContext2D, elementIndex: number) {
        const clippingPath = new Path2D();
        const [bounds] = this._applyAnimationToIndex(elementIndex);
        const { x, y, width: w, height: h } = bounds;
        const clampedRadii = this.#getClampedRadiiForBounds(bounds);
        clippingPath.roundRect(x, y, w, h, clampedRadii);
        context.clip(clippingPath);
    }

    /** Apply the animation to the bounds at the given index. This will return a new rectangle and alpha value. */
    _applyAnimationToIndex(index: number): [Rectangle, number] {
        const tempBounds = this._levelBounds[index].clone();

        // apply animation
        let xProgress = this.xProgress;
        let yProgress = this.yProgress;
        let alpha = DEFAULT_TEXT_ALPHA;
        this.animationConfigurations.forEach((animation) => {
            switch (animation.property) {
                case 'xProgress':
                    xProgress = animation.computedValues[index];
                    break;
                case 'yProgress':
                    yProgress = animation.computedValues[index];
                    break;
                case 'x': {
                    const offset = animation.shouldOffsetPropertyValue ? tempBounds.x : 0;
                    tempBounds.x = animation.computedValues[index] + offset;
                    break;
                }
                case 'y': {
                    const offset = animation.shouldOffsetPropertyValue ? tempBounds.y : 0;
                    tempBounds.y = animation.computedValues[index] + offset;
                    break;
                }
                case 'alpha':
                    alpha = animation.computedValues[index];
                    break;
            }
        });

        // apply progress
        const xDirection = applyXZigZagToDirection(this.xDirection, index);
        const yDirection = applyYZigZagToDirection(this.yDirection, index);
        applyXProgressToRectangle(tempBounds, xProgress, xDirection);
        applyYProgressToRectangle(tempBounds, yProgress, yDirection);

        return [tempBounds, alpha];
    }

    override get _numberOfShapes(): number {
        return this._levelBounds.length;
    }

    override _getOffsetForProperty(
        property: AnimatableBoxProperties,
        shouldOffsetPropertyValue: boolean,
    ): number {
        let offset = 0;
        switch (property) {
            case 'xProgress':
                if (shouldOffsetPropertyValue) {
                    offset = this.xProgress;
                }
                break;
            case 'yProgress':
                if (shouldOffsetPropertyValue) {
                    offset = this.yProgress;
                }
                break;
            case 'x':
            case 'y':
                // offset is handled during draw
                break;
            case 'alpha':
                if (shouldOffsetPropertyValue) {
                    offset = DEFAULT_TEXT_ALPHA;
                }
                break;
            default:
                throw new Error(
                    `Cannot apply offset to driven range for unsupported property "${property}"`,
                );
        }
        return offset;
    }
}

const applyXZigZagToDirection = (direction: DynamicXDirection, index: number) => {
    switch (direction) {
        case DynamicXDirection.zigZagLeft:
            return index % 2 === 0 ? DynamicXDirection.left : DynamicXDirection.right;
        case DynamicXDirection.zigZagRight:
            return index % 2 === 0 ? DynamicXDirection.right : DynamicXDirection.left;
    }
    return direction;
};

const applyYZigZagToDirection = (direction: DynamicYDirection, index: number) => {
    switch (direction) {
        case DynamicYDirection.zigZagTop:
            return index % 2 === 0 ? DynamicYDirection.top : DynamicYDirection.bottom;
        case DynamicYDirection.zigZagBottom:
            return index % 2 === 0 ? DynamicYDirection.bottom : DynamicYDirection.top;
    }
    return direction;
};

const applyXProgressToRectangle = (
    rectangle: Rectangle,
    progress: number,
    direction: DynamicXDirection,
) => {
    const { width } = rectangle;
    rectangle.width = width * progress;
    switch (direction) {
        case DynamicXDirection.left:
            rectangle.x += (1 - progress) * width;
            break;
        case DynamicXDirection.center:
            rectangle.x += ((1 - progress) * width) / 2;
            break;
        case DynamicXDirection.right:
            break;
    }
};

const applyYProgressToRectangle = (
    rectangle: Rectangle,
    progress: number,
    direction: DynamicYDirection,
) => {
    const { height } = rectangle;
    rectangle.height = height * progress;
    switch (direction) {
        case DynamicYDirection.top:
            rectangle.y += (1 - progress) * height;
            break;
        case DynamicYDirection.center:
            rectangle.y += ((1 - progress) * height) / 2;
            break;
        case DynamicYDirection.bottom:
            break;
    }
};

const drawRoundRect = (
    context: CanvasRenderingContext2D,
    rectangle: Rectangle,
    radii: Radii,
    fill: string | CanvasGradient,
) => {
    const { x, y, width, height } = rectangle;
    context.fillStyle = fill;
    context.beginPath();
    context.roundRect(x, y, width, height, radii);
    context.fill();
};

const drawStroke = (
    context: CanvasRenderingContext2D,
    strokeWidth: number,
    strokeStyle: RgbaString,
) => {
    context.lineWidth = strokeWidth;
    context.strokeStyle = strokeStyle;
    context.stroke();
};
